import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMe } from '../lib/useMe'
import { AppShell } from '../components/AppShell'
import { PageLoading } from '../components/AuthLayout'
import { RunningStatus, ResultsStatus } from '../components/RunStatus'
import {
  ApiError,
  type BrandDetail,
  type ModelId,
  type Run,
  type SuggestedPrompt,
  panels as panelsApi,
  runs as runsApi,
  sites as sitesApi,
  wizard as wizardApi,
} from '../lib/api'

// The onboarding wizard: domain -> brand details -> prompts -> configure -> review ->
// run -> results. One stateful page rather than one route per step (see
// cto-as-a-service docs/plans/2026-09-02-mentiontrend-onboarding-wizard.md §1) — a hard
// refresh mid-wizard loses progress, which is an accepted tradeoff for now: nothing here
// is destructive (no Site/Panel exists until its own explicit create call), so the worst
// case is re-entering a few fields, not lost or duplicated data.

type Step = 'domain' | 'brand' | 'prompts' | 'configure' | 'review' | 'running' | 'results'

const ALL_MODELS: { id: ModelId; label: string }[] = [
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' },
]
const ALL_LANGUAGES = [
  { id: 'nl', label: 'Nederlands' },
  { id: 'en', label: 'Engels' },
]

const PROMPT_KIND_LABELS: Record<string, string> = {
  core: 'Kern',
  exploration: 'Verkennend',
  comparative: 'Vergelijkend',
}

const STEP_LABELS: { step: Step; label: string }[] = [
  { step: 'domain', label: 'Domein' },
  { step: 'brand', label: 'Merkgegevens' },
  { step: 'prompts', label: 'Prompts' },
  { step: 'configure', label: 'Instellingen' },
  { step: 'review', label: 'Overzicht' },
  { step: 'running', label: 'Eerste meting' },
]

const RUN_POLL_INTERVAL_MS = 3000

// Wizard progress (prompts, chosen languages/models/repeats, competitors, which step and
// how far reached) lives only in this component's React state until the Panel is actually
// created at the review step — a refresh anywhere before that point used to lose it
// outright. sessionStorage closes that gap for resume mode (/sites/:id/setup, where the
// site id is a stable key survives a refresh via the URL itself) — NOT the fresh
// /onboarding flow, where no id exists yet to key a snapshot by until the very first
// domain submit, and losing a not-yet-submitted domain string is a trivial re-type, not
// real lost work.
interface WizardSnapshot {
  step: Step
  maxStepIndex: number
  prompts: SuggestedPrompt[]
  languages: string[]
  models: ModelId[]
  repeats: number
  competitors: { name: string; domain: string }[]
}

function wizardSnapshotKey(siteId: number): string {
  return `mt-wizard-progress-${siteId}`
}

// isWizardSnapshot runtime-checks the parsed JSON's shape before anything trusts it — a
// bare `as WizardSnapshot` cast doesn't actually verify anything at runtime, and this
// session alone has changed this exact shape more than once. A stale snapshot written by
// an older deploy, still sitting in an open tab's sessionStorage across a redeploy, would
// otherwise load fine as far as TypeScript is concerned and then crash the wizard the
// moment something like prompts.map() runs against a field that silently isn't an array.
function isWizardSnapshot(v: unknown): v is WizardSnapshot {
  if (typeof v !== 'object' || v === null) return false
  const s = v as Record<string, unknown>
  return (
    typeof s.step === 'string' &&
    typeof s.maxStepIndex === 'number' &&
    Array.isArray(s.prompts) &&
    Array.isArray(s.languages) &&
    Array.isArray(s.models) &&
    typeof s.repeats === 'number' &&
    Array.isArray(s.competitors)
  )
}

function loadWizardSnapshot(siteId: number): WizardSnapshot | null {
  try {
    const raw = sessionStorage.getItem(wizardSnapshotKey(siteId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isWizardSnapshot(parsed) ? parsed : null
  } catch {
    return null
  }
}

function saveWizardSnapshot(siteId: number, snapshot: WizardSnapshot) {
  try {
    sessionStorage.setItem(wizardSnapshotKey(siteId), JSON.stringify(snapshot))
  } catch {
    // sessionStorage can throw (quota, private/incognito mode) — losing the autosave is
    // not worth crashing the wizard over.
  }
}

function clearWizardSnapshot(siteId: number) {
  try {
    sessionStorage.removeItem(wizardSnapshotKey(siteId))
  } catch {
    // best effort
  }
}

export function Onboarding() {
  const navigate = useNavigate()
  const me = useMe()
  // Present only for /sites/:id/setup (resume an existing site), not /onboarding (fresh).
  // Resume always starts at step 2, never step 3 — caught during the app-shell plan's own
  // self-review: a site can reach this page with no brand details at all (the manual
  // quick-add form on Sites.tsx never calls brand-detail-from-domain), so skipping
  // straight to prompt suggestion would generate them from a domain-as-name and nothing
  // else. Step 2 is one click through when the data's already good, and never silently
  // wrong when it isn't.
  const { id: resumeSiteIdParam } = useParams<{ id?: string }>()
  const resumeSiteId = resumeSiteIdParam ? Number(resumeSiteIdParam) : null

  const [step, setStepRaw] = useState<Step>(resumeSiteId ? 'brand' : 'domain')
  // maxStepIndex tracks the FURTHEST step ever reached, separate from `step` (the
  // currently-displayed one). Without this, clicking a tab to go back made `step` regress,
  // which made every step ahead of it look "not yet reached" again and lose its click
  // handler — you could go back, but never forward again except by re-walking the whole
  // form. Resume mode starts past "domain" since it's skipped entirely.
  const [maxStepIndex, setMaxStepIndex] = useState(() => STEP_LABELS.findIndex((s) => s.step === (resumeSiteId ? 'brand' : 'domain')))
  function setStep(next: Step) {
    setStepRaw(next)
    markReachable(next)
  }
  // markReachable bumps maxStepIndex WITHOUT changing the currently-displayed step —
  // needed for resume mode: loading an existing panel means prompts/configure/review are
  // already valid and saved, so their tabs should be clickable immediately, even though
  // the visible step still starts at "brand" (one click through when the data's already
  // good — see the comment on resumeSiteId above). Previously maxStepIndex only ever
  // advanced via setStep, so a resumed site with a real panel still showed every later
  // tab as locked until you manually re-walked the whole wizard once.
  function markReachable(step: Step) {
    const idx = STEP_LABELS.findIndex((s) => s.step === step)
    if (idx >= 0) setMaxStepIndex((prev) => Math.max(prev, idx))
  }
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loadingResume, setLoadingResume] = useState(!!resumeSiteId)
  // restoredFromSnapshot: whether THIS page load restored not-yet-saved local edits
  // (rather than the site's actual saved state) — surfaced so a visitor who abandoned an
  // earlier edit and comes back later isn't silently stuck looking at it with no way to
  // get back to what's really saved.
  const [restoredFromSnapshot, setRestoredFromSnapshot] = useState(false)

  const [domain, setDomain] = useState('')
  const [siteId, setSiteId] = useState<number | null>(resumeSiteId)

  const [brand, setBrand] = useState<BrandDetail>({ name: '', description: '', industry: '', region: '' })
  const [brandFetchFailed, setBrandFetchFailed] = useState(false)
  const [autoFetchSkipped, setAutoFetchSkipped] = useState(false)
  // fetchingBrand is its own flag rather than reusing the fresh flow's "busy && brand.name
  // === ''" proxy for "a fetch is in flight" — that proxy breaks in resume mode, where
  // brand.name is never empty to begin with (it's pre-filled from the Site row). Caught
  // directly: without this, clicking "Automatisch ophalen" while resuming showed no
  // loading state at all, just a disabled button.
  const [fetchingBrand, setFetchingBrand] = useState(false)
  // justFetchedBrand: resume mode suppresses the "Opgehaald van [domain]" badge by
  // default (this session doesn't know the existing data's real provenance — see
  // showProvenanceBadge below) UNLESS the visitor explicitly triggers a fetch THIS
  // session, in which case the provenance is known for certain.
  const [justFetchedBrand, setJustFetchedBrand] = useState(false)

  const [prompts, setPrompts] = useState<SuggestedPrompt[]>([])
  const [newPromptText, setNewPromptText] = useState('')

  const [languages, setLanguages] = useState<string[]>(['nl'])
  const [models, setModels] = useState<ModelId[]>(['chatgpt', 'claude', 'gemini'])
  const [repeats, setRepeats] = useState(1)
  const [competitors, setCompetitors] = useState<{ name: string; domain: string }[]>([])
  const [newCompetitorName, setNewCompetitorName] = useState('')
  const [newCompetitorDomain, setNewCompetitorDomain] = useState('')

  const [panelId, setPanelId] = useState<number | null>(null)
  const [runId, setRunId] = useState<number | null>(null)
  const [run, setRun] = useState<Run | null>(null)

  useEffect(() => {
    if (me.isError) navigate('/login')
  }, [me.isError, navigate])

  // Resume mode: load the site's current brand details (whatever they are — possibly
  // empty, see the comment on resumeSiteId above) and, if a panel already exists,
  // pre-fill prompts/languages/models/repeats too, so "Bewerken" from the site-detail
  // page doesn't mean retyping a working configuration from scratch.
  useEffect(() => {
    if (!resumeSiteId) return
    let cancelled = false
    async function loadForResume() {
      try {
        const site = await sitesApi.get(resumeSiteId as number)
        if (cancelled) return
        setDomain(site.domain)
        setBrand({ name: site.name, description: site.description, industry: site.industry, region: site.region })
        // A site with no description and a name still equal to its domain has never been
        // through step 2 at all — the "name = domain" fallback createSite uses until a
        // real fetch or manual entry fills it in (see sites.go). That's genuinely a first
        // setup, not a resume with real typed data to protect, so it's safe to auto-fetch
        // the same way the fresh (non-resume) flow already does — no manual click needed.
        const neverSetUp = !site.description && site.name === site.domain
        if (neverSetUp) {
          void runBrandFetch(resumeSiteId as number)
        }
        if (site.panel) {
          // A saved panel means prompts/configure/review are all already valid — unlock
          // their tabs immediately instead of forcing a full re-walk of the wizard just to
          // re-earn tabs that were already reachable in a previous session.
          markReachable('review')
        }
        const snapshot = loadWizardSnapshot(resumeSiteId as number)
        if (snapshot) {
          // Local, not-yet-saved edits from earlier in THIS browser tab take priority over
          // the DB's saved panel (if any) — they're by definition more recent than
          // whatever was last actually persisted, which is exactly the "refresh lost my
          // prompts" gap this exists to close.
          setStepRaw(snapshot.step)
          setMaxStepIndex((prev) => Math.max(prev, snapshot.maxStepIndex))
          setPrompts(snapshot.prompts)
          setLanguages(snapshot.languages)
          setModels(snapshot.models)
          setRepeats(snapshot.repeats)
          setCompetitors(snapshot.competitors)
          setRestoredFromSnapshot(true)
        } else if (site.panel) {
          setPrompts(site.panel.prompts.map((p) => ({ text: p.text, kind: p.kind as SuggestedPrompt['kind'] })))
          setLanguages(site.panel.languages)
          setModels(site.panel.models)
          setRepeats(site.panel.repeats)
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Kon de site niet laden.')
      } finally {
        if (!cancelled) setLoadingResume(false)
      }
    }
    loadForResume()
    return () => {
      cancelled = true
    }
  }, [resumeSiteId])

  function discardSnapshot() {
    if (!resumeSiteId) return
    clearWizardSnapshot(resumeSiteId)
    // Simplest correct reset: every field this restores lives only in this component's
    // state, spread across several branches above — re-deriving all of it inline risks
    // missing one, where a full reload is guaranteed to land back on the real saved state.
    window.location.reload()
  }

  // Autosave: fires on every change to the fields a refresh used to wipe out, but only
  // once the initial load (from a snapshot or the DB) has actually finished — otherwise
  // this would immediately overwrite a real snapshot with the empty defaults every field
  // starts at before loadForResume runs.
  useEffect(() => {
    if (!resumeSiteId || loadingResume) return
    saveWizardSnapshot(resumeSiteId, { step, maxStepIndex, prompts, languages, models, repeats, competitors })
  }, [resumeSiteId, loadingResume, step, maxStepIndex, prompts, languages, models, repeats, competitors])

  // --- step 1: domain -----------------------------------------------------------------

  async function submitDomain(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const site = await sitesApi.create(domain)
      setSiteId(site.id)
      setStep('brand')
      await runBrandFetch(site.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de site niet aanmaken.')
    } finally {
      setBusy(false)
    }
  }

  // Skips the auto-fetch entirely (plan §1.1's "vul alles handmatig in" escape hatch) —
  // distinct from BrandStep's retry-after-failure path: this one is a deliberate choice
  // made before any fetch is even attempted, not a fallback from one that failed.
  async function submitDomainManual() {
    if (!domain.trim()) {
      setError('Vul een domein in.')
      return
    }
    setError('')
    setBusy(true)
    try {
      const site = await sitesApi.create(domain)
      setSiteId(site.id)
      setAutoFetchSkipped(true) // reuses the manual-entry form; no fetch was ever attempted
      setStep('brand')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de site niet aanmaken.')
    } finally {
      setBusy(false)
    }
  }

  // --- step 2: brand details -----------------------------------------------------------

  async function runBrandFetch(id: number) {
    setBusy(true)
    setFetchingBrand(true)
    setBrandFetchFailed(false)
    setAutoFetchSkipped(false)
    try {
      const detail = await wizardApi.fetchBrandDetail(id)
      setBrand(detail)
      setJustFetchedBrand(true)
    } catch {
      // Any failure (timeout, Studio error, malformed output) degrades to manual entry —
      // never a hard block, per the wizard plan's Step2-FetchFailed design.
      setBrandFetchFailed(true)
    } finally {
      setBusy(false)
      setFetchingBrand(false)
    }
  }

  async function submitBrand(e: React.FormEvent) {
    e.preventDefault()
    if (!siteId) return
    setError('')
    setBusy(true)
    try {
      const result = await wizardApi.suggestPrompts(siteId, { ...brand, language: languages[0] ?? 'nl', count: 5 })
      setPrompts(result.prompts ?? [])
      setStep('prompts')
    } catch (err) {
      // Fails open per plan §4a: an empty suggestion list is not an error state, the
      // manual "+ Toevoegen" row is always available on the next step regardless.
      setPrompts([])
      setStep('prompts')
      void err
    } finally {
      setBusy(false)
    }
  }

  // --- step 3: prompts -------------------------------------------------------------------

  function addPrompt() {
    const text = newPromptText.trim()
    if (!text) return
    setPrompts((prev) => [...prev, { text, kind: 'core' }])
    setNewPromptText('')
  }

  function removePrompt(index: number) {
    setPrompts((prev) => prev.filter((_, i) => i !== index))
  }

  function editPrompt(index: number, text: string) {
    setPrompts((prev) => prev.map((p, i) => (i === index ? { ...p, text } : p)))
  }

  function submitPrompts(e: React.FormEvent) {
    e.preventDefault()
    if (prompts.length === 0) {
      setError('Voeg minstens één prompt toe.')
      return
    }
    setError('')
    setStep('configure')
  }

  // --- step 4: configure -----------------------------------------------------------------

  function toggleLanguage(id: string) {
    setLanguages((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]))
  }
  function toggleModel(id: ModelId) {
    setModels((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]))
  }
  function addCompetitor() {
    const name = newCompetitorName.trim()
    if (!name) return
    setCompetitors((prev) => [...prev, { name, domain: newCompetitorDomain.trim() }])
    setNewCompetitorName('')
    setNewCompetitorDomain('')
  }
  function removeCompetitor(index: number) {
    setCompetitors((prev) => prev.filter((_, i) => i !== index))
  }

  function submitConfigure(e: React.FormEvent) {
    e.preventDefault()
    if (languages.length === 0) {
      setError('Kies minstens één taal.')
      return
    }
    if (models.length === 0) {
      setError('Kies minstens één model.')
      return
    }
    setError('')
    setStep('review')
  }

  // --- step 5: review + start the run -----------------------------------------------------

  const totalTurns = prompts.length * languages.length * models.length * repeats
  const estimatedCredits = Math.round(totalTurns * 5.6)

  async function startRun() {
    if (!siteId) return
    setError('')
    setBusy(true)
    try {
      const panel = await panelsApi.create({
        site_id: siteId,
        prompts: prompts.map((p) => ({ text: p.text, kind: p.kind })),
        languages,
        models,
        repeats,
        competitors: competitors.length > 0 ? competitors : undefined,
      })
      setPanelId(panel.id)
      // The panel is now the real, saved source of truth — a stale local snapshot from
      // this point on could only ever contradict it (e.g. after a later "Bewerken" edit
      // gets abandoned mid-way and someone reopens this same site fresh).
      if (resumeSiteId) clearWizardSnapshot(resumeSiteId)
      const started = await panelsApi.run(panel.id)
      setRunId(started.run_id)
      setStep('running')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de meting niet starten.')
    } finally {
      setBusy(false)
    }
  }

  // --- step 6/7: poll the run until it's done ---------------------------------------------

  useEffect(() => {
    if (step !== 'running' || runId === null) return
    let cancelled = false

    async function poll() {
      try {
        const r = await runsApi.get(runId as number)
        if (cancelled) return
        setRun(r)
        if (r.status === 'succeeded' || r.status === 'failed' || r.status === 'refunded') {
          setStep('results')
          return
        }
      } catch {
        // A transient poll failure just tries again on the next tick.
      }
      if (!cancelled) setTimeout(poll, RUN_POLL_INTERVAL_MS)
    }
    poll()
    return () => {
      cancelled = true
    }
  }, [step, runId])

  // Both checks are required, not just the redirect effect above: React commits a render
  // BEFORE effects run, so a lone `if (me.isLoading) return null` falls through and
  // renders the full wizard (interactive, though every API call it makes would 401) for
  // one frame after the session check resolves to unauthenticated and before the
  // redirect effect fires. Confirmed live: GET /app/onboarding with no session cookie
  // serves the SPA shell at 200 (expected, client-side routing) and briefly showed step
  // 1's form before redirecting. Matches the guard Dashboard.tsx and Sites.tsx already
  // have; this page was missing the second line.
  if (me.isLoading) return <PageLoading />
  if (me.isError) return null
  if (loadingResume) return <PageLoading title="Site laden…" />

  return (
    <div>
      <AppShell />
      <div className="mx-auto max-w-4xl px-6 py-14">
        <h1 className="mb-2 text-2xl font-semibold tracking-tight text-ink">
          {resumeSiteId ? `${brand.name || domain} instellen` : 'Nieuwe site instellen'}
        </h1>
        <Stepper current={step} maxStepIndex={maxStepIndex} onNavigate={setStep} />
      {error && (
        <div className="mb-4 rounded-lg border border-[#C1666B] bg-[#C1666B22] px-4 py-2.5 text-sm text-ink">{error}</div>
      )}
      {restoredFromSnapshot && (
        <div className="mb-4 rounded-lg border border-amber bg-[#E8A33D22] px-4 py-2.5 text-sm text-ink">
          Niet-opgeslagen wijzigingen van eerder hersteld.{' '}
          <button type="button" onClick={discardSnapshot} className="text-amber underline">
            Verwerpen en opnieuw beginnen
          </button>
        </div>
      )}

      {step === 'domain' && (
        <DomainStep
          domain={domain}
          setDomain={setDomain}
          onSubmit={submitDomain}
          onSkip={submitDomainManual}
          busy={busy}
        />
      )}

      {step === 'brand' && (
        <BrandStep
          domain={domain}
          brand={brand}
          setBrand={setBrand}
          fetching={fetchingBrand}
          fetchFailed={brandFetchFailed}
          autoFetchSkipped={autoFetchSkipped}
          // Resume mode never went through a fresh Studio fetch this session — showing
          // "Opgehaald van [domain]" would be misleading for a site whose name is just
          // its domain default (the manual quick-add path) or whose data was typed by
          // hand on an earlier visit. Once the new "automatisch ophalen" button in resume
          // mode actually runs a fresh fetch, the badge becomes true again.
          showProvenanceBadge={!resumeSiteId || justFetchedBrand}
          isResume={!!resumeSiteId}
          onRetry={() => siteId && runBrandFetch(siteId)}
          onSubmit={submitBrand}
          busy={busy}
        />
      )}

      {step === 'prompts' && (
        <PromptsStep
          prompts={prompts}
          newPromptText={newPromptText}
          setNewPromptText={setNewPromptText}
          onAdd={addPrompt}
          onRemove={removePrompt}
          onEdit={editPrompt}
          onSubmit={submitPrompts}
        />
      )}

      {step === 'configure' && (
        <ConfigureStep
          languages={languages}
          models={models}
          repeats={repeats}
          competitors={competitors}
          newCompetitorName={newCompetitorName}
          setNewCompetitorName={setNewCompetitorName}
          newCompetitorDomain={newCompetitorDomain}
          setNewCompetitorDomain={setNewCompetitorDomain}
          onAddCompetitor={addCompetitor}
          onRemoveCompetitor={removeCompetitor}
          onToggleLanguage={toggleLanguage}
          onToggleModel={toggleModel}
          setRepeats={setRepeats}
          onSubmit={submitConfigure}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          domain={domain}
          promptCount={prompts.length}
          languages={languages}
          models={models}
          repeats={repeats}
          totalTurns={totalTurns}
          estimatedCredits={estimatedCredits}
          balance={me.data?.credits ?? 0}
          busy={busy}
          onBack={() => setStep('configure')}
          onStart={startRun}
        />
      )}

      {step === 'running' && <RunningStatus run={run} />}

      {step === 'results' && panelId !== null && (
        <ResultsStatus
          run={run}
          doneLabel={`Naar ${brand.name || 'site'}`}
          onDone={() => siteId && navigate(`/sites/${siteId}`)}
          onRetryFailed={(languages) => panelId && retryRun(panelId, languages)}
          busy={busy}
        />
      )}
      </div>
    </div>
  )

  // languages: when set, scopes the retry to only those (plan §5.9 — "opnieuw proberen"
  // on a partial failure must not re-run, and re-charge for, what already succeeded).
  // Omitted for a full failure, where nothing succeeded and there is nothing to preserve.
  async function retryRun(id: number, languages?: string[]) {
    setError('')
    setBusy(true)
    try {
      const started = await panelsApi.run(id, languages)
      setRunId(started.run_id)
      setRun(null)
      setStep('running')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de meting niet opnieuw starten.')
    } finally {
      setBusy(false)
    }
  }
}

function Stepper({
  current,
  maxStepIndex,
  onNavigate,
}: {
  current: Step
  maxStepIndex: number
  onNavigate: (step: Step) => void
}) {
  const idx = STEP_LABELS.findIndex((s) => s.step === current)
  return (
    <div className="mb-8 flex flex-wrap items-center gap-2 border-b border-line pb-5 text-sm">
      {STEP_LABELS.map((s, i) => {
        const isCurrent = i === idx
        const isDone = i <= maxStepIndex
        // "domain" can never be a nav target once a site exists: its form re-submit
        // unconditionally calls sitesApi.create(domain), so clicking into it would silently
        // create a duplicate site. "running" is a transient action tab, not an editable
        // form, so it's excluded too. Every other reached step is safe to revisit — their
        // submit handlers only update the existing site/panel, never create a new one.
        const clickable = isDone && !isCurrent && s.step !== 'domain' && s.step !== 'running'
        return (
          <button
            key={s.step}
            type="button"
            disabled={!clickable}
            onClick={() => clickable && onNavigate(s.step)}
            className={
              'flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors ' +
              (isCurrent
                ? 'bg-amber font-semibold text-[#0E2A3B]'
                : isDone
                  ? clickable
                    ? 'text-ink hover:bg-card hover:text-amber cursor-pointer'
                    : 'text-ink'
                  : 'text-ink-soft cursor-default')
            }
          >
            <span
              className={
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ' +
                (isCurrent
                  ? 'bg-[#0E2A3B] text-amber'
                  : isDone
                    ? 'bg-amber text-[#0E2A3B]'
                    : 'border border-line text-ink-soft')
              }
            >
              {i + 1}
            </span>
            {s.label}
          </button>
        )
      })}
    </div>
  )
}

function DomainStep({
  domain,
  setDomain,
  onSubmit,
  onSkip,
  busy,
}: {
  domain: string
  setDomain: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  onSkip: () => void
  busy: boolean
}) {
  return (
    <form onSubmit={onSubmit}>
      <p className="mb-4 text-ink-soft">Met welk domein wil je beginnen?</p>
      <input
        type="text"
        required
        autoFocus
        placeholder="jouwbedrijf.nl"
        className="mb-4 w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
      />
      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={busy}
          className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Bezig…' : 'Verder'}
        </button>
        <button type="button" disabled={busy} onClick={onSkip} className="text-sm text-ink-soft underline hover:text-ink">
          Vul alles handmatig in
        </button>
      </div>
    </form>
  )
}

function BrandStep({
  domain,
  brand,
  setBrand,
  fetching,
  fetchFailed,
  autoFetchSkipped,
  showProvenanceBadge,
  isResume,
  onRetry,
  onSubmit,
  busy,
}: {
  domain: string
  brand: BrandDetail
  setBrand: (b: BrandDetail) => void
  fetching: boolean
  fetchFailed: boolean
  autoFetchSkipped: boolean
  showProvenanceBadge: boolean
  isResume: boolean
  onRetry: () => void
  onSubmit: (e: React.FormEvent) => void
  busy: boolean
}) {
  if (fetching) {
    return <p className="text-ink-soft">We lezen de website uit…</p>
  }
  return (
    <form onSubmit={onSubmit}>
      {fetchFailed && (
        <div className="mb-4 rounded-lg border border-amber bg-[#E8A33D22] px-4 py-2.5 text-sm text-ink">
          We konden de site niet automatisch uitlezen. Vul de gegevens hieronder handmatig in.{' '}
          <button type="button" onClick={onRetry} className="text-amber underline">
            Nog een keer proberen
          </button>
        </div>
      )}
      {autoFetchSkipped && (
        <div className="mb-4 rounded-lg border border-line bg-card px-4 py-2.5 text-sm text-ink-soft">
          Vul de merkgegevens hieronder handmatig in.
        </div>
      )}
      {(!fetchFailed && !autoFetchSkipped && showProvenanceBadge && brand.name) || (isResume && !fetchFailed) ? (
        <div className="mb-4 flex items-center gap-2">
          {!fetchFailed && !autoFetchSkipped && showProvenanceBadge && brand.name && (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1 text-xs text-ink-soft">
              Opgehaald van {domain}
            </div>
          )}
          {/* Resume mode with real, already-typed data never auto-fetches — overwriting it
              without being asked would be worse than leaving it alone (a genuinely
              first-time site, name still equal to its domain, DOES auto-fetch — see the
              loadForResume effect above). This icon is the manual re-fetch entry point for
              a site that already has real data but the visitor wants refreshed anyway. */}
          {isResume && !fetchFailed && (
            <button
              type="button"
              onClick={onRetry}
              disabled={busy}
              title={`Automatisch ophalen van ${domain}`}
              aria-label={`Automatisch ophalen van ${domain}`}
              className="rounded-full border border-line p-1.5 text-ink-soft hover:border-amber hover:text-amber disabled:opacity-50"
            >
              <RefreshIcon />
            </button>
          )}
        </div>
      ) : null}
      <label className="mb-4 block">
        <span className="mb-1.5 block text-sm text-ink-soft">Bedrijfsnaam</span>
        <input
          type="text"
          required
          className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
          value={brand.name}
          onChange={(e) => setBrand({ ...brand, name: e.target.value })}
        />
      </label>
      <label className="mb-4 block">
        <span className="mb-1.5 block text-sm text-ink-soft">Korte omschrijving</span>
        <textarea
          rows={3}
          className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
          value={brand.description}
          onChange={(e) => setBrand({ ...brand, description: e.target.value })}
        />
      </label>
      <div className="mb-4 flex gap-4">
        <label className="block flex-1">
          <span className="mb-1.5 block text-sm text-ink-soft">Branche</span>
          <input
            type="text"
            className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={brand.industry}
            onChange={(e) => setBrand({ ...brand, industry: e.target.value })}
          />
        </label>
        <label className="block flex-1">
          <span className="mb-1.5 block text-sm text-ink-soft">Regio</span>
          <input
            type="text"
            className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={brand.region}
            onChange={(e) => setBrand({ ...brand, region: e.target.value })}
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
      >
        {busy ? 'Bezig…' : 'Prompts voorstellen'}
      </button>
    </form>
  )
}

function PromptsStep({
  prompts,
  newPromptText,
  setNewPromptText,
  onAdd,
  onRemove,
  onEdit,
  onSubmit,
}: {
  prompts: SuggestedPrompt[]
  newPromptText: string
  setNewPromptText: (v: string) => void
  onAdd: () => void
  onRemove: (i: number) => void
  onEdit: (i: number, text: string) => void
  onSubmit: (e: React.FormEvent) => void
}) {
  return (
    <form onSubmit={onSubmit}>
      <p className="mb-4 text-ink-soft">Wat vraag je aan AI om te checken? Bewerk, verwijder wat niet past, of voeg je eigen prompt toe.</p>
      <ul className="mb-4 flex flex-col gap-2">
        {prompts.map((p, i) => (
          <li key={i} className="flex items-center gap-3 rounded-lg border border-line bg-card px-4 py-3 focus-within:border-amber">
            <span className="flex-shrink-0 rounded-md border border-amber px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber">
              {PROMPT_KIND_LABELS[p.kind] ?? p.kind}
            </span>
            <input
              type="text"
              aria-label={`Bewerk prompt ${i + 1}`}
              value={p.text}
              onChange={(e) => onEdit(i, e.target.value)}
              className="flex-1 bg-transparent text-sm text-ink focus:outline-none"
            />
            <button
              type="button"
              aria-label={`Verwijder prompt: ${p.text}`}
              onClick={() => onRemove(i)}
              className="text-ink-soft hover:text-[#e08a90]"
            >
              ×
            </button>
          </li>
        ))}
        {prompts.length === 0 && <li className="text-ink-soft">Nog geen prompts. Voeg er hieronder een toe.</li>}
      </ul>
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Schrijf je eigen prompt"
          className="w-full rounded-lg border border-dashed border-line bg-transparent px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
          value={newPromptText}
          onChange={(e) => setNewPromptText(e.target.value)}
        />
        <button
          type="button"
          onClick={onAdd}
          className="whitespace-nowrap rounded-lg border border-dashed border-line px-4 py-2.5 text-ink-soft hover:border-amber hover:text-amber"
        >
          + Toevoegen
        </button>
      </div>
      <button type="submit" className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90">
        Instellingen kiezen
      </button>
    </form>
  )
}

function ConfigureStep({
  languages,
  models,
  repeats,
  competitors,
  newCompetitorName,
  setNewCompetitorName,
  newCompetitorDomain,
  setNewCompetitorDomain,
  onAddCompetitor,
  onRemoveCompetitor,
  onToggleLanguage,
  onToggleModel,
  setRepeats,
  onSubmit,
}: {
  languages: string[]
  models: ModelId[]
  repeats: number
  competitors: { name: string; domain: string }[]
  newCompetitorName: string
  setNewCompetitorName: (v: string) => void
  newCompetitorDomain: string
  setNewCompetitorDomain: (v: string) => void
  onAddCompetitor: () => void
  onRemoveCompetitor: (i: number) => void
  onToggleLanguage: (id: string) => void
  onToggleModel: (id: ModelId) => void
  setRepeats: (n: number) => void
  onSubmit: (e: React.FormEvent) => void
}) {
  return (
    <form onSubmit={onSubmit}>
      <div className="mb-4">
        <span className="mb-2 block text-sm text-ink-soft">Talen</span>
        <div className="flex gap-4">
          {ALL_LANGUAGES.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-ink">
              <input type="checkbox" checked={languages.includes(l.id)} onChange={() => onToggleLanguage(l.id)} />
              {l.label}
            </label>
          ))}
        </div>
      </div>
      <div className="mb-4">
        <span className="mb-2 block text-sm text-ink-soft">Modellen</span>
        <div className="flex gap-4">
          {ALL_MODELS.map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-ink">
              <input type="checkbox" checked={models.includes(m.id)} onChange={() => onToggleModel(m.id)} />
              {m.label}
            </label>
          ))}
        </div>
      </div>
      <label className="mb-6 block max-w-[200px]">
        <span className="mb-1.5 block text-sm text-ink-soft">Herhalingen per prompt</span>
        <input
          type="number"
          min={1}
          max={5}
          className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
          value={repeats}
          onChange={(e) => setRepeats(Math.max(1, Number(e.target.value) || 1))}
        />
      </label>
      <div className="mb-6">
        <span className="mb-2 block text-sm text-ink-soft">Concurrenten (optioneel)</span>
        {competitors.length > 0 && (
          <ul className="mb-2 flex flex-col gap-2">
            {competitors.map((c, i) => (
              <li key={i} className="flex items-center gap-3 rounded-lg border border-line bg-card px-4 py-2.5 text-sm">
                <span className="flex-1 text-ink">
                  {c.name}
                  {c.domain && <span className="text-ink-soft"> — {c.domain}</span>}
                </span>
                <button
                  type="button"
                  aria-label={`Verwijder concurrent: ${c.name}`}
                  onClick={() => onRemoveCompetitor(i)}
                  className="text-ink-soft hover:text-[#e08a90]"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Naam concurrent"
            className="w-full rounded-lg border border-dashed border-line bg-transparent px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={newCompetitorName}
            onChange={(e) => setNewCompetitorName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Domein (optioneel)"
            className="w-full rounded-lg border border-dashed border-line bg-transparent px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={newCompetitorDomain}
            onChange={(e) => setNewCompetitorDomain(e.target.value)}
          />
          <button
            type="button"
            onClick={onAddCompetitor}
            className="whitespace-nowrap rounded-lg border border-dashed border-line px-4 py-2.5 text-ink-soft hover:border-amber hover:text-amber"
          >
            + Toevoegen
          </button>
        </div>
      </div>
      <button type="submit" className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90">
        Overzicht bekijken
      </button>
    </form>
  )
}

function ReviewStep({
  domain,
  promptCount,
  languages,
  models,
  repeats,
  totalTurns,
  estimatedCredits,
  balance,
  busy,
  onBack,
  onStart,
}: {
  domain: string
  promptCount: number
  languages: string[]
  models: ModelId[]
  repeats: number
  totalTurns: number
  estimatedCredits: number
  balance: number
  busy: boolean
  onBack: () => void
  onStart: () => void
}) {
  const insufficientCredits = balance < estimatedCredits
  return (
    <div>
      <div className="mb-6 rounded-xl border border-line bg-card p-5 text-sm text-ink">
        <dl className="flex flex-col gap-2">
          <div className="flex justify-between">
            <dt className="text-ink-soft">Domein</dt>
            <dd>{domain}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-soft">Prompts</dt>
            <dd>{promptCount}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-soft">Talen</dt>
            <dd>{languages.join(', ')}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-soft">Modellen</dt>
            <dd>{models.join(', ')}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-soft">Herhalingen</dt>
            <dd>{repeats}</dd>
          </div>
          <div className="flex justify-between border-t border-line pt-2 font-semibold">
            <dt>Totaal metingen</dt>
            <dd>{totalTurns}</dd>
          </div>
          <div className="flex justify-between font-semibold">
            <dt>Geschatte credits</dt>
            <dd>{estimatedCredits}</dd>
          </div>
          <div className="flex justify-between text-ink-soft">
            <dt>Jouw saldo</dt>
            <dd>{balance}</dd>
          </div>
        </dl>
      </div>
      {insufficientCredits && (
        <div className="mb-4 rounded-lg border border-[#C1666B] bg-[#C1666B22] px-4 py-2.5 text-sm text-ink">
          Onvoldoende credits voor deze meting.
        </div>
      )}
      <div className="flex gap-3">
        <button type="button" onClick={onBack} className="rounded-lg border border-line px-4 py-2.5 text-ink-soft hover:text-ink">
          ← Terug
        </button>
        <button
          type="button"
          onClick={onStart}
          disabled={busy || insufficientCredits}
          className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Bezig…' : 'Start eerste meting'}
        </button>
      </div>
    </div>
  )
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  )
}

